import * as z from "@-/com.npmjs.zod";

export const workerQueryMethodSchema = z.enum(["run", "all", "values", "get"]);

export const workerQueryRequestBodySchema = z.object({
  method: workerQueryMethodSchema,
  params: z.array(z.unknown()),
  // Absent means positional row arrays; `object` asks for column-keyed rows (see worker handler).
  rowMode: z.optional(z.enum(["array", "object"])),
  sql: z.string(),
});

export type WorkerQueryRequestBody = z.infer<typeof workerQueryRequestBodySchema>;

/** Worker `/query` response — `rows` shape depends on `method` (see worker handler). */
export const workerQueryResponseBodySchema = z.object({
  rows: z.unknown(),
});

export type WorkerQueryResponseBody = z.infer<typeof workerQueryResponseBodySchema>;

/**
 * `/query/batch` request — several independent statements carried in ONE round-trip.
 *
 * Every remote read costs the same ~110ms of network + D1 latency whether it returns one row or
 * sixty-four, so a page that issues a dozen small reads pays for a dozen round-trips it does not
 * need. Batching collapses concurrent reads into a single POST; the per-statement shape is
 * identical to {@link workerQueryRequestBodySchema} so the worker can reuse the same execution
 * path (including the read-only SQL guard) for each entry.
 */
export const workerBatchQueryRequestBodySchema = z.object({
  queries: z.array(workerQueryRequestBodySchema).min(1),
});

export type WorkerBatchQueryRequestBody = z.infer<typeof workerBatchQueryRequestBodySchema>;

/**
 * `/query/batch` response — one result per request statement, in request order.
 *
 * Each entry is either `{ rows }` (same shape and `method` semantics as the single-query
 * response, so `get`'s no-match `null` survives untouched) or `{ error }` when that ONE statement
 * failed. Per-statement errors keep one bad read from failing its unrelated neighbours, which
 * matters precisely because batching groups reads that have nothing to do with each other.
 */
export const workerBatchQueryResponseBodySchema = z.object({
  results: z.array(z.union([workerQueryResponseBodySchema, z.object({ error: z.string() })])),
});

export type WorkerBatchQueryResponseBody = z.infer<typeof workerBatchQueryResponseBodySchema>;

/** Server-side variant that requires `rows` to be an array (no `get`-style single value). */
export const workerQueryResponseBodyArrayRowsSchema = workerQueryResponseBodySchema.extend({
  rows: z.array(z.unknown()),
});
